import type {
  Corridor,
  CorridorModel,
  DeployedUnit,
  Deployment,
  MeasureAbsence,
  MeasureKey,
  MeasureLine,
  MeasureName,
  MeasureNames,
  Measures,
  MeasureZoneKey,
  Phase,
  PhaseLineName,
  UnitPosition,
} from "../../api/types";
import type { MeasureChange } from "../../api/client";
import { unitDesignation } from "../../i18n/designation";
import { k, locale, t } from "../../i18n/i18n";
import { isPlacedLevel } from "../../echelons";
import { isRails, LINE_NAMES, modifiedPairs, widthAlerts, type WidthContext, widths } from "../../map/manoeuvre/corridorEdit";
import { forLevel, formatKm, formatKmBound } from "../../map/manoeuvre/corridorWidths";
import {
  CFL_ADVANCE,
  LEVEL_LINES,
  LEVEL_ZONES,
  MEMENTO_DEPTH_KM,
  type MeasureLevel,
  type MeasureWarning,
  NAMES,
  P1,
  P2,
  PHASED,
  warningText,
} from "../../map/manoeuvre/zoMeasures";

/** Contenu du panneau Manœuvre (docs/14 §2.2), sans React : largeur du fuseau, paires modifiées et alerte de
 *  largeur, état des LIMA, des lignes et zones de la ZO (docs/15 §4.4, §4.5 ; aussi pour leur fiche), des unités
 *  et des étapes. */

export interface WidthSummary {
  min: number;
  max: number;
  /** Même largeur affichée partout (au centième de kilomètre près) : « 18,15 km » plutôt qu'une étendue. */
  uniform: boolean;
}

export function widthSummary(values: readonly number[]): WidthSummary | null {
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  return { min, max, uniform: Math.round(min / 10) === Math.round(max / 10) };
}

export interface CorridorFindings {
  widths: number[];
  /** Paires dont la largeur s'écarte de plus de 1 % de celle de l'amorce. */
  modified: number[];
  /** Paires en alerte (docs/15 §2.4) : retouchées et éloignées de la plage doctrinale par la retouche (toutes
   *  les paires hors de la plage pour un fuseau antérieur ou une zone libre). */
  alerts: number[];
  /** Largeur de la pire paire en alerte (m), null sans alerte. */
  worstWidth: number | null;
  /** Plage doctrinale [min, max] de la clé de gabarit (m), null sans clé du niveau. */
  range: [number, number] | null;
}

/** Paires modifiées et alerte de largeur (docs/14 §2.2, docs/15 §2.4 ; `context` : `widthContextOf` du fuseau
 *  reçu) : l'alerte ne vise que les paires retouchées que la retouche éloigne de la plage doctrinale de la clé
 *  de gabarit (`context.levelKey`) ; le calage du fuseau automatique sur les routes fait varier la largeur sans
 *  aucun geste, les paires non retouchées n'alertent donc jamais (sauf fuseau antérieur, `context.legacy`, ou
 *  zone libre sans amorce). Sans clé du niveau : aucune alerte. */
export function corridorFindings(
  model: CorridorModel,
  seedWidths: readonly number[] | null | undefined,
  context: WidthContext = {},
): CorridorFindings {
  const found = widths(model);
  const reference = seedWidths && seedWidths.length ? seedWidths : undefined;
  // Amorce de référence du serveur (`widthContextOf`) : même règle que lui pour les paires modifiées et l'alerte ;
  // sinon les largeurs de l'amorce (fuseau antérieur : son amorce de l'iPad).
  const rails = isRails(context.reference) ? context.reference : undefined;
  const modified = modifiedPairs(model, rails ?? reference, context.levelKey);
  const alert = widthAlerts(model, model.level, { ...context, reference: context.reference ?? reference ?? [] });
  const worstWidth = alert?.worst ?? null;
  return {
    widths: found,
    modified,
    alerts: alert?.alerts ?? [],
    worstWidth,
    range: alert ? [alert.doctrine.min_m, alert.doctrine.max_m] : null,
  };
}

/** Largeur par défaut de l'échelon (médiane du mémento) : celle du serveur, sinon celle de la clé de gabarit. */
export function templateWidth(deployment: Pick<Deployment, "corridor" | "level" | "level_key">): number | null {
  const fromServer = deployment.corridor?.template_width_m;
  if (fromServer) return fromServer;
  return forLevel(deployment.level, deployment.level_key)?.default_m ?? null;
}

/** Détail de la ligne de largeur du groupe « Fuseau » (docs/15 §4.5) : « largeur doctrinale 8,00 km · plage 6 à
 *  10 km », complété de « calée sur le terrain » tant qu'aucune paire n'est retouchée (le calage sur les routes fait
 *  varier la largeur sans aucun geste). Plage : celle du serveur (`width_range_m`), sinon celle des constats. */
export function widthDetail(
  doctrinal: number | null,
  range: readonly [number, number] | null | undefined,
  untouched: boolean,
): string | undefined {
  if (!doctrinal) return undefined;
  const parts = [t("largeur doctrinale {km}", { km: formatKm(doctrinal) })];
  if (range) parts.push(t("plage {min} à {max} km", { min: formatKmBound(range[0]), max: formatKmBound(range[1]) }));
  if (untouched) parts.push(t("calée sur le terrain"));
  return parts.join(" · ");
}

/** Fuseau tracé avant les largeurs du mémento (docs/15 §2.4) : géométrie gardée, toutes ses paires comparées à la
 *  plage ; le panneau l'annonce par un encadré et propose le fuseau automatique. */
export function legacyCorridor(corridor: Pick<Corridor, "width_basis" | "custom"> | null | undefined): boolean {
  return Boolean(corridor?.custom && corridor.width_basis === "legacy");
}

/** Ligne « Sens AMI → ENI » de l'étape Terrain (docs/14 §2.14) : état et caps à afficher, ou null (CENTAC, sans
 *  état). `zone` : terrain libre, sens fixé par l'axe tracé ; `default` : sens par défaut ; `oriented` : sens choisi
 *  appliqué ; `not_applied` : sens enregistré qui ne tient pas dans le camp pour cette mission (`tangled` : il y tient,
 *  mais le fuseau automatique recalé sur les routes serait invalide) ; `overflow` : appliqué au mieux, le dispositif
 *  déborde du camp (fuseau retouché gardé). */
export interface OrientationRowModel {
  state: "zone" | "default" | "oriented" | "not_applied" | "overflow";
  /** Sens appliqué (affiché dans le titre). */
  bearing: number;
  /** Sens par défaut (null en terrain libre). */
  template: number | null;
  /** Sens enregistré (null : sens par défaut). */
  requested: number | null;
  /** Le bouton « Orienter la manœuvre » est proposé (camp) ; `legacy` : bloqué, fuseau antérieur. */
  orientable: boolean;
  legacy: boolean;
  tangled: boolean;
}

export function orientationRow(deployment: Pick<Deployment, "corridor"> | null | undefined): OrientationRowModel | null {
  const block = deployment?.corridor?.orientation;
  if (!block) return null;
  if (block.reason === "zone") {
    return {
      state: "zone",
      bearing: block.bearing_deg,
      template: null,
      requested: null,
      orientable: false,
      legacy: false,
      tangled: false,
    };
  }
  const state =
    block.requested_deg !== null && !block.applied
      ? "not_applied"
      : block.applied && !block.fits
        ? "overflow"
        : block.applied
          ? "oriented"
          : "default";
  return {
    state,
    bearing: block.bearing_deg,
    template: block.template_deg,
    requested: block.requested_deg,
    orientable: true,
    legacy: block.reason === "legacy",
    tangled: state === "not_applied" && Boolean(block.tangled),
  };
}

export type LineState = "entered" | "moved" | "doctrinal";

/** État affiché d'une LIMA : GDH saisi, glissée, ou à sa position doctrinale. « Glissée » ne vient que du serveur
 *  (tracé glissé `displayMoved`, ou `moved` du modèle) : les courbures ne comptent pas, l'amorce du fuseau
 *  automatique en porte déjà (LIMA segmentées sur le terrain) sans aucun geste. */
export function lineState(
  model: CorridorModel | null | undefined,
  name: PhaseLineName,
  displayMoved: boolean,
): LineState {
  const line = model?.lines[name];
  if (line && line.hours !== null && line.hours !== undefined) return "entered";
  if (displayMoved || line?.moved) return "moved";
  return "doctrinal";
}

/** LIMA du panneau, dans l'ordre L0 → L3 : présentes (modèle éditable, à défaut tracé du serveur) ou absentes. */
export function limaRows(deployment: Pick<Deployment, "corridor">): { name: PhaseLineName; present: boolean }[] {
  const corridor = deployment.corridor;
  const model = corridor?.model;
  return LINE_NAMES.map((name) => ({
    name,
    present: model ? Boolean(model.lines[name]) : Boolean(corridor?.lines.some((l) => l.name === name)),
  }));
}

// --- Lignes et zones de la ZO (docs/15 §4.4, §4.5) -----------------------------------------------------------------

/** État d'une ligne de la ZO au temps affiché : calculée d'après le dispositif (état normal, texte gris), déplacée
 *  (position choisie), retirée (CFL, FSCL) ou sans objet (aucun tracé : raison en texte gris). */
export type MeasureState = "computed" | "moved" | "off" | "absent";

export const MEASURE_STATE_LABEL: Record<MeasureState, string> = {
  computed: k("Calculée"),
  moved: k("Déplacée"),
  off: k("Retirée"),
  absent: k("Sans objet"),
};

export interface MeasureRow {
  key: MeasureKey;
  /** Temps de la ligne : celui affiché pour la FLOT et la CFL, null pour les autres. */
  phase: Phase | null;
  /** Tracé au temps affiché ; null pour une ligne retirée ou sans objet. */
  line: MeasureLine | null;
  state: MeasureState;
  /** Raison de « Sans objet », null dans les autres états. */
  reason: Exclude<MeasureAbsence["reason"], "off"> | null;
  /** Noms du serveur (ceux du catalogue du niveau pour une ligne sans tracé). */
  names: MeasureNames;
}

/** Niveau des lignes de la ZO (compagnie, régiment, brigade, division) ; null en CENTAC. */
export function measureLevel(level: string | null | undefined): MeasureLevel | null {
  return isPlacedLevel(level) ? level : null;
}

/** Lignes de la ZO du niveau au temps affiché, dans l'ordre du catalogue : limites (arrière, internes, avant), puis
 *  FLOT, CFL et FSCL. Une ligne retirée l'est à tous les temps ; une ligne sans tracé ni absence (hors du niveau)
 *  n'a pas de rangée. */
export function measureRows(measures: Measures, level: MeasureLevel, phase: Phase): MeasureRow[] {
  const rows: MeasureRow[] = [];
  for (const key of LEVEL_LINES[level]) {
    const rowPhase = PHASED.includes(key) ? phase : null;
    const line = measures.lines.find((l) => l.key === key && l.phase === rowPhase) ?? null;
    const names = line?.names ?? NAMES[level][key];
    if (!names) continue;
    const base = { key, phase: rowPhase, names };
    if ((key === "CFL" && measures.cfl_off) || (key === "FSCL" && measures.fscl_off)) {
      rows.push({ ...base, line: null, state: "off", reason: null });
    } else if (line) {
      rows.push({ ...base, line, state: line.moved ? "moved" : "computed", reason: null });
    } else {
      const absence = measures.absent.find((a) => a.key === key && (a.phase === rowPhase || a.phase === null));
      if (absence && absence.reason !== "off") rows.push({ ...base, line: null, state: "absent", reason: absence.reason });
    }
  }
  return rows;
}

/** Nom d'une ligne ou d'une zone dans la langue du dossier (comme L0 / PL0), à défaut en français. */
export function measureName(names: MeasureNames, language: "fr" | "en"): MeasureName {
  return names[language] ?? names.fr;
}

/** Temps où la position de la FLOT ou de la CFL est choisie (`placement.measures`). */
export function movedPhases(measures: Measures, key: MeasureKey): Phase[] {
  if (key !== "FLOT" && key !== "CFL") return [];
  const entry = measures.overrides[key] as Partial<Record<Phase, unknown>> | undefined;
  return (["T1", "T2", "T3"] as Phase[]).filter((phase) => Boolean(entry?.[phase]));
}

/** Au moins une position choisie ou une ligne retirée : « Revenir aux positions doctrinales » a un effet. */
export function measuresTouched(measures: Measures): boolean {
  return measures.cfl_off || measures.fscl_off || Object.keys(measures.overrides ?? {}).some((key) => key !== "v");
}

/** Point de la ligne où centrer la carte : son milieu (9e des 17 points). */
export function measureCenter(line: Pick<MeasureLine, "coordinates">): [number, number] | null {
  const points = line.coordinates;
  return points.length ? (points[Math.floor(points.length / 2)] ?? null) : null;
}

/** Écriture de « Revenir à la position doctrinale » : rétablir une ligne retirée, ou rendre calculée une position
 *  choisie (au temps de la rangée pour la FLOT et la CFL) ; null pour une ligne déjà calculée. */
export function revertChange(row: Pick<MeasureRow, "key" | "phase" | "state">): MeasureChange | null {
  if (row.state === "off") return { key: row.key, off: false };
  if (row.state !== "moved") return null;
  return PHASED.includes(row.key) ? { key: row.key, phase: row.phase, ends: null } : { key: row.key, ends: null };
}

/** Nom d'une ligne dans un avis ou l'historique : « FLOT (T2) » pour la FLOT et la CFL, sinon le nom court. */
export function measureLabel(row: Pick<MeasureRow, "phase" | "names">, language: "fr" | "en"): string {
  const short = measureName(row.names, language).short;
  return row.phase ? `${short} (${row.phase})` : short;
}

/** Écriture complète d'une action de fiche ou de panneau : modification envoyée, libellé d'historique et texte de
 *  l'avis. `remove` : retirer la CFL ou la FSCL (à tous les temps) ; `revert` : « Revenir à la position
 *  doctrinale ». null si l'action est sans objet pour cette ligne. */
export function measureAction(
  row: MeasureRow,
  action: "remove" | "revert",
  language: "fr" | "en",
): { change: MeasureChange; label: string; text: string } | null {
  if (action === "remove") {
    // Une ligne sans tracé au temps affiché (retirée, sans objet) n'a rien à retirer ici : le retrait vaut pour tous
    // les temps, il se propose là où la ligne est visible.
    if ((row.key !== "CFL" && row.key !== "FSCL") || !row.line) return null;
    const line = measureName(row.names, language).short;
    return { change: { key: row.key, off: true }, label: t("retrait de {line}", { line }), text: t("{line} retirée", { line }) };
  }
  const change = revertChange(row);
  if (!change) return null;
  if (row.state === "off") {
    const line = measureName(row.names, language).short;
    return { change, label: t("rétablissement de {line}", { line }), text: t("{line} rétablie", { line }) };
  }
  const line = measureLabel(row, language);
  return {
    change,
    label: t("position doctrinale de {line}", { line }),
    text: t("{line} : position doctrinale rétablie", { line }),
  };
}

const percent = (fraction: number) => fraction.toLocaleString(locale(), { style: "percent", maximumFractionDigits: 0 });

type MeasuresForCfl = Pick<Measures, "lines" | "inputs" | "overrides" | "fscl_off">;

/** Ce qui borne la CFL calculée d'un temps, côté par côté (`zo_measures.compute`) : c = min(F + Δ ; 1 ; FSCL − g),
 *  F la FLOT effective, Δ l'avance de la CFL, g l'écart minimal entre lignes, FSCL la FSCL choisie (division).
 *  « free » : à F + Δ ; « limit » : bornée par la limite avant ; « fscl » : bornée par la FSCL choisie. null sans
 *  FLOT au temps donné. */
export function cflBounds(
  measures: MeasuresForCfl,
  phase: Phase,
): { flot: readonly [number, number]; values: [number, number]; sides: ("free" | "limit" | "fscl")[] } | null {
  const flot = measures.lines.find((l) => l.key === "FLOT" && l.phase === phase)?.ends;
  if (!flot) return null;
  const { gap: g, cfl_gap: delta } = measures.inputs;
  const fscl = measures.fscl_off ? undefined : measures.overrides.FSCL?.ends;
  const values = [0, 0] as [number, number];
  const sides = ([0, 1] as const).map((side) => {
    const free = flot[side] + delta;
    const limit = Math.min(free, 1);
    const value = fscl ? Math.min(limit, fscl[side] - g) : limit;
    values[side] = value;
    if (value >= free - 1e-9) return "free" as const;
    return fscl && fscl[side] - g < limit ? ("fscl" as const) : ("limit" as const);
  });
  return { flot, values, sides };
}

/** Raison de « Sans objet » (`row` au temps affiché) : aucune unité amie placée (FLOT, et CFL faute de FLOT), ou plus
 *  de place devant la FLOT pour la CFL (moins de g) : FSCL choisie trop proche en division, sinon limite avant
 *  atteinte. La cause est celle du côté qui manque de place (`cflBounds`), et non la seule présence d'une FSCL
 *  choisie : une FSCL au-delà de la limite avant laisse la limite avant arrêter la CFL. */
export function absenceReason(row: Pick<MeasureRow, "reason" | "phase">, measures: MeasuresForCfl): string {
  if (row.reason === "no_unit") return t("aucune unité amie placée");
  const bounds = row.phase ? cflBounds(measures, row.phase) : null;
  const g = measures.inputs.gap;
  const short = bounds ? bounds.sides.filter((_, side) => bounds.values[side]! - bounds.flot[side]! < g) : [];
  if (short.includes("fscl")) return t("plus de place entre la FLOT et la FSCL");
  if (!bounds && measures.overrides.FSCL?.ends && !measures.fscl_off) return t("plus de place entre la FLOT et la FSCL");
  return t("la FLOT atteint la limite avant");
}

/** Position d'une ligne de la ZO (fiche, panneau, docs/15 §3.2 et §4.4) : la règle qui la place. `unitName` : nom
 *  affiché de l'unité qui porte la FLOT calculée (à défaut, sa désignation). */
export function measurePosition(
  row: MeasureRow,
  measures: Pick<Measures, "lines" | "inputs" | "axis_length_m" | "overrides" | "fscl_off">,
  level: MeasureLevel,
  unitName: (unit: string) => string | undefined = () => undefined,
): string {
  if (row.state === "absent") return absenceReason(row, measures);
  if (row.state === "off") return t("retirée de la carte et des exports");
  if (row.key === "LIMAR") return t("extrémité arrière du fuseau");
  if (row.key === "LIMAV") return t("extrémité avant du fuseau");
  if (row.state === "moved") return t("position choisie sur la carte");
  const basis = row.line?.basis ?? null;
  switch (row.key) {
    case "FLOT":
      return basis && "unit" in basis
        ? t("à hauteur de {unit}", { unit: unitDesignation(unitName(basis.unit) || basis.designation) })
        : t("à hauteur de l'unité amie la plus avancée");
    case "CFL": {
      // Bornée par la limite avant ou par la FSCL choisie (FLOT proche de la limite avant, au T3 d'une mission
      // offensive notamment) : écart réel à la FLOT, et ce qui l'arrête.
      const bounds = row.phase && row.line ? cflBounds(measures, row.phase) : null;
      if (bounds && bounds.sides.some((side) => side !== "free")) {
        const ends = row.line!.ends;
        const km = formatKm((((ends[0] - bounds.flot[0]) + (ends[1] - bounds.flot[1])) / 2) * measures.axis_length_m);
        return bounds.sides.includes("fscl")
          ? t("{km} en avant de la FLOT, bornée par la FSCL", { km })
          : t("{km} en avant de la FLOT, bornée par la limite avant", { km });
      }
      const gap = measures.inputs.cfl_gap;
      return gap > CFL_ADVANCE + 1e-9
        ? t("{km} en avant de la FLOT (écart minimal)", { km: formatKm(gap * measures.axis_length_m) })
        : t("{pct} de la profondeur de la ZO en avant de la FLOT", { pct: percent(gap) });
    }
    case "FSCL":
      return t("au-delà de la limite avant de la ZO, dans la profondeur du corps d'armée");
    default:
      break;
  }
  if (!basis || !("rule" in basis)) return t("proportion doctrinale, ajustée au dispositif");
  switch (basis.rule) {
    case "proportion": {
      const share = (row.key === "ZOAR_ZOR" ? P1 : P2)[level];
      if (level === "BRIGADE" || share === undefined) return t("proportion doctrinale : mi-profondeur de la ZO");
      return t("proportion doctrinale : {km} km sur {total}", {
        km: Math.round(share * MEMENTO_DEPTH_KM),
        total: MEMENTO_DEPTH_KM,
      });
    }
    case "flot":
      return basis.phase
        ? t("au-delà de la FLOT la plus avancée ({phase})", { phase: basis.phase })
        : t("au-delà de la FLOT la plus avancée");
    case "maneuver":
      return t("en deçà de l'unité de manœuvre la plus en arrière");
    case "min_depth":
      return t("profondeur minimale de zone");
    case "neighbour":
      return t("bornée par une ligne déplacée");
  }
}

/** Profondeur d'une ligne : distance moyenne à la limite arrière, ou au-delà de la limite avant (FSCL) ; null pour
 *  la limite arrière. */
export function measureDepth(line: Pick<MeasureLine, "key" | "ends">, axisLength: number): string | null {
  if (line.key === "LIMAR") return null;
  const s = (line.ends[0] + line.ends[1]) / 2;
  return s > 1 + 1e-9
    ? t("{km} au-delà de la limite avant", { km: formatKm((s - 1) * axisLength) })
    : t("{km} depuis la limite arrière", { km: formatKm(s * axisLength) });
}

/** Profondeur de chaque zone du niveau : « ZOR BIA 12,40 km · ZOP BIA 9,80 km » (noms dans la langue du dossier). */
export function zoneDepths(measures: Measures, level: MeasureLevel, language: "fr" | "en"): string | null {
  const parts: string[] = [];
  for (const key of Object.keys(LEVEL_ZONES[level]) as MeasureZoneKey[]) {
    const fraction = measures.depth_fractions[key];
    const names = measures.zones.find((z) => z.key === key)?.names ?? NAMES[level][key];
    if (fraction === undefined || !names) continue;
    parts.push(`${measureName(names, language).short} ${formatKm(fraction * measures.axis_length_m)}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** Note du pied du groupe : d'où viennent les profondeurs, par niveau (docs/15 §4.5). */
export function depthNote(level: MeasureLevel): string {
  if (level === "DIVISION")
    return t(
      "Profondeurs : proportions doctrinales (ZOAR 200 km, ZOR 150 km, ZOP 100 km), ajustées au dispositif. Le camp est moins profond que la ZO doctrinale.",
    );
  if (level === "CORPS")
    return t("Profondeurs : proportions doctrinales (ZOAR 200 km, ZOR 450 km, ZOP 150 km), ajustées au dispositif ; la ZOR du corps est faite des zones des divisions.");
  if (level === "BRIGADE") return t("Profondeurs : ZOR et ZOP à parts égales, selon la doctrine, ajustées au dispositif.");
  if (level === "COMPAGNIE")
    return t("La ZO du S/GTIA va de la limite arrière à la limite avant de sa zone ; la FLOT suit le dispositif, la CFL est celle du GTIA.");
  return t("La ZO du GTIA va de la limite arrière à la limite avant du fuseau ; la FLOT et la CFL suivent le dispositif.");
}

/** Lignes concernées par chaque avertissement du serveur (`zo_measures.WARNINGS`). */
const WARNING_LINES: Record<MeasureWarning, readonly MeasureKey[]> = {
  flot_beyond_cfl: ["FLOT", "CFL"],
  units_beyond_flot: ["FLOT"],
  units_beyond_limit: ["LIMAV", "FLOT"],
  fscl_short_of_flot: ["FSCL", "FLOT"],
};

/** Avertissements du serveur qui concernent une ligne (au temps donné pour la FLOT et la CFL, à tous les temps pour
 *  les autres), en français : à afficher par `tServer`. */
export function lineWarnings(warnings: readonly string[], key: MeasureKey, phase: Phase | null): string[] {
  const phases: Phase[] = phase ? [phase] : ["T1", "T2", "T3"];
  const found: string[] = [];
  for (const [kind, keys] of Object.entries(WARNING_LINES) as [MeasureWarning, readonly MeasureKey[]][]) {
    if (!keys.includes(key)) continue;
    for (const p of phases) {
      const text = warningText(kind, p);
      if (warnings.includes(text) && !found.includes(text)) found.push(text);
    }
  }
  return found;
}

export type UnitState = "doctrinal" | "anchored" | "moved";

/** État d'une unité à un temps : déplacée à la main, placée selon la mission (ancrage), ou position doctrinale. */
export function unitState(position: Pick<UnitPosition, "moved" | "anchored"> | undefined): UnitState {
  if (position?.moved) return "moved";
  if (position?.anchored) return "anchored";
  return "doctrinal";
}

/** Unités d'un camp listées par le panneau : dessinées (non masquées), au temps donné ou à l'un des temps. */
export function unitsOf(units: readonly DeployedUnit[] | undefined, side: "AMI" | "ENI", phase?: Phase): DeployedUnit[] {
  return (units ?? []).filter(
    (u) => u.side === side && !u.hidden && (phase ? Boolean(u.positions[phase]) : Object.keys(u.positions).length > 0),
  );
}

export type StepState = "done" | "active" | "todo";

/** Pastilles d'état des étapes (§2.2) : faite (coche bleu ami), en cours (anneau animé), à faire (anneau gris). */
export function stepStates(
  deployment: Pick<Deployment, "corridor" | "units">,
  ui: { editing: boolean; selectedLine: boolean },
): Record<"zone" | "fuseau" | "lima" | "unites" | "missions", StepState> {
  const corridor = deployment.corridor;
  const model = corridor?.model;
  const units = deployment.units.filter((u) => !u.hidden);
  const positions = units.flatMap((u) => Object.values(u.positions).filter((p): p is UnitPosition => Boolean(p)));
  const limaTouched = LINE_NAMES.some((name) => {
    const display = corridor?.lines.find((l) => l.name === name);
    return lineState(model, name, Boolean(display?.moved)) !== "doctrinal";
  });
  return {
    zone: "done",
    fuseau: ui.editing ? "active" : corridor?.custom ? "done" : "todo",
    lima: ui.editing && ui.selectedLine ? "active" : limaTouched ? "done" : "todo",
    unites: positions.some((p) => p.moved || p.anchored) ? "done" : "todo",
    missions: positions.some((p) => p.overridden) ? "done" : "todo",
  };
}
